import React, { useMemo, useRef, useState } from "react";
import {
  computePaddedCanvasLayout,
  formatPaddingCornerRadiiCss,
  normalizePaddingSettings,
} from "../../../../../utils/canvasPaddingLayout";
import {
  aspectFromSize,
  computeFittedStageSize,
  TOOL_PREP_STAGE_INSET,
  useImageNaturalSize,
  useReportSubjectPlacement,
  useToolPrepPreviewViewport,
} from "../../../lib/toolPrepImageLayout";
import RoundLoader from "../../../../../components/ui/RoundLoader";
import ToolPrepSubjectBoundingBox from "../../../shared/Popup/ToolPrepSubjectBoundingBox";
import {
  ToolPrepSubjectCropGhost,
  subjectMainImageStyleForCrop,
  subjectWrapperStyleForCrop,
} from "../../../shared/Popup/ToolPrepSubjectCropGhost";
import SoftWatermarkOverlay from "../../../shared/Popup/SoftWatermarkOverlay";
import { softWatermarkEdgeInsetForCornerRadius } from "../../../../../config/tools/softWatermarkBrand";
import { useToolPrepSubjectObject } from "../../../hooks/useToolPrepSubjectObject";
import ToolPrepCanvasStage from "../../../shared/Popup/ToolPrepCanvasStage";
import "../../../shared/Popup/ToolPrepStage.css";
import "./PaddingPreview.css";

const EMPTY_STAGE_ASPECT = 1;

function computePaddingPreviewMetrics(
  viewport,
  naturalSize,
  settings,
  imageUrl
) {
  const vw = viewport.width;
  const vh = viewport.height;

  if (!vw || !vh) {
    return {
      displaySize: { width: 0, height: 0 },
      paddedLayout: null,
      scale: 1,
      fillColor: "transparent",
      useChecker: true,
    };
  }

  const normalized = normalizePaddingSettings(settings);
  const fillColor = normalized.fillColor;
  const useChecker = !fillColor || fillColor === "transparent";

  if (!imageUrl) {
    const displaySize =
      computeFittedStageSize(vw, vh, EMPTY_STAGE_ASPECT, {
        inset: TOOL_PREP_STAGE_INSET,
      }) ?? { width: 420, height: 420 };
    return {
      displaySize,
      paddedLayout: null,
      scale: 1,
      fillColor,
      useChecker: true,
    };
  }

  const imageAspect = aspectFromSize(
    naturalSize.width,
    naturalSize.height,
    EMPTY_STAGE_ASPECT
  );

  const baseStageSize =
    computeFittedStageSize(vw, vh, imageAspect, {
      inset: TOOL_PREP_STAGE_INSET,
    }) ?? { width: 0, height: 0 };

  if (!baseStageSize.width || !naturalSize.width || !naturalSize.height) {
    const displaySize =
      computeFittedStageSize(vw, vh, imageAspect, {
        inset: TOOL_PREP_STAGE_INSET,
      }) ?? { width: 0, height: 0 };
    return {
      displaySize,
      paddedLayout: null,
      scale: 1,
      fillColor,
      useChecker,
    };
  }

  const paddedLayout = computePaddedCanvasLayout(
    baseStageSize.width,
    baseStageSize.height,
    naturalSize.width,
    naturalSize.height,
    normalized
  );

  const outputAspect =
    paddedLayout?.canvasWidth && paddedLayout?.canvasHeight
      ? paddedLayout.canvasWidth / paddedLayout.canvasHeight
      : imageAspect;

  const displaySize =
    computeFittedStageSize(vw, vh, outputAspect, {
      inset: TOOL_PREP_STAGE_INSET,
    }) ?? { width: 0, height: 0 };

  const scale =
    paddedLayout?.canvasWidth && displaySize.width
      ? displaySize.width / paddedLayout.canvasWidth
      : 1;

  return {
    displaySize,
    paddedLayout,
    scale,
    fillColor,
    useChecker,
  };
}

/**
 * Live preview — canvas expands around the image (same model as CanvasEditor).
 */
const PaddingPreview = ({
  imageUrl = null,
  settings = null,
  loading = false,
  applying = false,
  resultMode = false,
  resultImageUrl = null,
  emptyLabel = "Upload an image to preview",
  showSubjectBox = false,
  softWatermarkToolKey = "padding",
}) => {
  const stageRef = useRef(null);
  const naturalSize = useImageNaturalSize(imageUrl);
  const viewport = useToolPrepPreviewViewport(stageRef);

  const metrics = useMemo(
    () =>
      computePaddingPreviewMetrics(
        viewport,
        naturalSize,
        settings,
        imageUrl
      ),
    [viewport, naturalSize, settings, imageUrl]
  );

  const { displaySize, paddedLayout, scale, fillColor, useChecker } = metrics;

  const imageStyle = useMemo(() => {
    if (!paddedLayout?.imageWidth) return undefined;
    const borderRadius = formatPaddingCornerRadiiCss(
      paddedLayout.imageCornerRadii,
      scale
    );
    return {
      width: paddedLayout.imageWidth * scale,
      height: paddedLayout.imageHeight * scale,
      left: paddedLayout.imageX * scale,
      top: paddedLayout.imageY * scale,
      borderRadius,
    };
  }, [paddedLayout, scale]);

  const seedFrame = useMemo(() => {
    if (!imageStyle?.width) return null;
    return {
      x: imageStyle.left,
      y: imageStyle.top,
      width: imageStyle.width,
      height: imageStyle.height,
    };
  }, [imageStyle?.left, imageStyle?.top, imageStyle?.width, imageStyle?.height]);

  const interactive = showSubjectBox && Boolean(imageUrl) && !resultMode;
  const [isCropping, setIsCropping] = useState(false);
  const {
    object,
    setObject,
    wrapperStyle,
    imageStyle: cropImgStyle,
    contentCrop,
    crop,
    cropExtent,
    originalImageSize,
  } = useToolPrepSubjectObject({
      enabled: interactive,
      imageUrl,
      fitFrame: seedFrame,
      resetKey: imageUrl,
    });

  useReportSubjectPlacement(
    null,
    object,
    contentCrop,
    displaySize,
    imageUrl
  );

  const cornerRadiusPx = (paddedLayout?.cornerRadiusPx ?? 0) * scale;
  // Lock frame to live-preview displaySize so Apply does not jump the canvas.
  const showResult = resultMode && resultImageUrl && displaySize.width > 0;
  const softWmEdgeInset = softWatermarkEdgeInsetForCornerRadius(cornerRadiusPx);

  // Only round the outer canvas when there is a solid fill. For transparent
  // fill, keep the canvas square so the checker shows in the corners instead
  // of the dark page background punching through (looks like black marks).
  const canvasStyle = {
    width: displaySize.width,
    height: displaySize.height,
    ...(showResult
      ? {}
      : {
          borderRadius:
            cornerRadiusPx > 0 && !useChecker
              ? `${cornerRadiusPx}px`
              : undefined,
          backgroundColor: useChecker ? undefined : fillColor,
        }),
  };

  return (
    <ToolPrepCanvasStage
      className="padding-preview"
      ref={stageRef}
      metaImageUrl={imageUrl}
      metaWidth={paddedLayout?.canvasWidth || naturalSize.width}
      metaHeight={paddedLayout?.canvasHeight || naturalSize.height}
    >
      <div
        className={`padding-preview__canvas${
          showResult
            ? " padding-preview__canvas--result"
            : useChecker
              ? " padding-preview__canvas--checker"
              : ""
        }`}
        style={canvasStyle}
      >
        <div className="tool-prep-stage__clip">
          {loading ? (
            <div className="padding-preview__loading">
              <RoundLoader size="md" label="Removing background…" />
            </div>
          ) : applying ? (
            <div className="padding-preview__loading">
              <RoundLoader size="md" label="Applying padding…" />
            </div>
          ) : showResult ? (
            <>
              <img
                src={resultImageUrl}
                alt="Padded result"
                className="padding-preview__result"
                draggable={false}
              />
              <div
                className="padding-preview__wm-clip"
                style={
                  cornerRadiusPx > 0
                    ? { borderRadius: `${cornerRadiusPx}px` }
                    : undefined
                }
              >
                <SoftWatermarkOverlay
                  toolKey={softWatermarkToolKey}
                  edgeInset={softWmEdgeInset}
                />
              </div>
            </>
          ) : imageUrl && interactive && object && wrapperStyle ? (
            <div
              className="padding-preview__subject"
              style={subjectWrapperStyleForCrop(
                {
                  ...wrapperStyle,
                  borderRadius: imageStyle?.borderRadius,
                },
                isCropping
              )}
            >
              <ToolPrepSubjectCropGhost
                active={isCropping}
                src={imageUrl}
                style={cropImgStyle}
              />
              <img
                src={imageUrl}
                alt="Padding preview"
                className="padding-preview__image"
                style={subjectMainImageStyleForCrop(cropImgStyle, {
                  isCropping,
                  crop,
                  originalImageSize,
                  object,
                })}
                draggable={false}
              />
            </div>
          ) : imageUrl ? (
            <img
              src={imageUrl}
              alt="Padding preview"
              className={
                imageStyle
                  ? "padding-preview__image"
                  : "padding-preview__image padding-preview__image--contain"
              }
              style={imageStyle}
              draggable={false}
            />
          ) : (
            <div className="padding-preview__empty">{emptyLabel}</div>
          )}
        </div>
        {imageUrl && interactive && object && wrapperStyle && !showResult ? (
          <div className="tool-prep-stage__chrome">
            <ToolPrepSubjectBoundingBox
              enabled
              interactive
              frame={object}
              crop={crop}
              cropExtent={cropExtent}
              originalImageSize={originalImageSize}
              onChange={setObject}
              onCroppingChange={setIsCropping}
              stageSize={displaySize}
            />
          </div>
        ) : null}
      </div>
    </ToolPrepCanvasStage>
  );
};

export default PaddingPreview;
